import { rankGamesByFrequency } from "../platform/storage/ranking";
import type { ArcadeStateV1 } from "../platform/storage/schema";
import type { ExternalTapCounts } from "../platform/storage/external-taps";
import { buildHomeSlots, type GameRegistry, type HomeSlot } from "./game-registry";
import type { ExternalGameLink } from "./rosebud-catalog";

export function createHomeSlotsForState(
  registry: GameRegistry,
  state: ArcadeStateV1,
  minimumPositions = 12,
): readonly HomeSlot[] {
  const rankedGames = rankGamesByFrequency(registry.listHomeGames(), state);
  // Pagination happens after source-tier ordering. The launcher grid size is a
  // minimum fill target, never a cap that can discard primary CARCADE games.
  return buildHomeSlots(rankedGames, Math.max(minimumPositions, rankedGames.length));
}

/**
 * Owner instruction: "games reorder by frequency" -- with no first-party/
 * external carve-out. The prior carve-out (external tiles frozen in
 * scrape-snapshot order forever) traced to an agent-written spec page, not
 * an owner statement; see wiki/product/local-ranking.md's corrected text.
 *
 * Curated non-Rosebud web games are a separate external source tier. Keep
 * them in curated order ahead of Rosebud so a newly selected web game cannot
 * disappear onto a later page merely because this browser has historical
 * Rosebud tap counts. Rosebud entries still rank by real per-title taps first,
 * then committed snapshot order, then slug for determinism.
 */
export function rankExternalGamesByFrequency(
  games: readonly ExternalGameLink[],
  tapCounts: ExternalTapCounts,
): ExternalGameLink[] {
  const isCuratedWebExternal = (game: ExternalGameLink): boolean =>
    !game.url.startsWith("https://rosebud.ai/play/");

  const curated = games.filter(isCuratedWebExternal);
  const rankedRosebud = games
    .filter((game) => !isCuratedWebExternal(game))
    .map((game, catalogOrder) => ({ game, catalogOrder }))
    .sort(
      (left, right) =>
        (tapCounts[right.game.slug] ?? 0) - (tapCounts[left.game.slug] ?? 0) ||
        left.catalogOrder - right.catalogOrder ||
        left.game.slug.localeCompare(right.game.slug),
    )
    .map(({ game }) => game);

  return [...curated, ...rankedRosebud];
}
